import React from 'react';
import styled, { keyframes } from 'styled-components';

// Keyframe Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const bounceIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const drawCheck = keyframes`
  0% {
    stroke-dasharray: 100;
    stroke-dashoffset: 100;
  }
  100% {
    stroke-dasharray: 100;
    stroke-dashoffset: 0;
  }
`;

const pingAnimation = keyframes`
  75%, 100% {
    transform: scale(2);
    opacity: 0;
  }
`;

const pulseAnimation = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: .5;
  }
`;

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background-color: #292D2E;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const BackgroundParticles = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
`;

const Particle = styled.div`
  position: absolute;
  width: 0.25rem;
  height: 0.25rem;
  border-radius: 50%;
  animation: ${pulseAnimation} 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  
  &:nth-child(1) {
    background-color: #34d399;
    top: 25%;
    left: 25%;
  }
  
  &:nth-child(2) {
    background-color: #60a5fa;
    top: 75%;
    right: 25%;
    animation-delay: 1s;
  }
  
  &:nth-child(3) {
    background-color: #a78bfa;
    top: 50%;
    left: 75%;
    animation-delay: 2s;
  }
`;

const Card = styled.div`
  position: relative;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 1.5rem;
  padding: 4rem;
  text-align: center;
  max-width: 32rem;
  width: 100%;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: ${fadeInUp} 0.8s ease-out;
`;

const IconWrapper = styled.div`
  position: relative;
  width: 8rem;
  height: 8rem;
  margin: 0 auto 2rem;
  background: linear-gradient(135deg, #34d399, #059669);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${bounceIn} 1s ease-out;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const PingBorder = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border-radius: 50%;
  border: 2px solid rgba(52, 211, 153, 0.3);
  animation: ${pingAnimation} 1s cubic-bezier(0, 0, 0.2, 1) infinite;
`;

const CheckIcon = styled.svg`
  width: 4rem;
  height: 4rem;
  color: white;
  stroke: currentColor;
  fill: none;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
  animation: ${drawCheck} 0.8s ease-out 0.5s forwards;
`;

const Title = styled.h1`
  font-size: 2.25rem;
  font-weight: 700;
  color: white;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #ffffff, #e2e8f0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;
`;

const Subtitle = styled.p`
  font-size: 1.125rem;
  color: #cbd5e1;
  margin-bottom: 3rem;
  line-height: 1.625;
  animation: ${fadeInUp} 0.8s ease-out 0.4s both;
`;

const CloseButton = styled.button`
  position: relative;
  background: linear-gradient(90deg, rgb(255, 215, 0), rgb(255, 140, 0));
  box-shadow: rgb(255, 168, 0) 0px -2px inset, 0 8px 24px rgba(255, 168, 0, 0.3);
  color: #1f2937;
  border: none;
  padding: 1rem 2rem;
  border-radius: 0.75rem;
  font-weight: 700;
  font-size: 1.125rem;
  cursor: pointer;
  transition: all 0.3s ease;
  animation: ${fadeInUp} 0.8s ease-out 0.6s both;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-0.25rem);
    
    .hover-overlay {
      opacity: 1;
    }
    
    .shine-effect {
      transform: translateX(100%);
    }
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ButtonText = styled.span`
  position: relative;
  z-index: 10;
  color: #1f2937;
  font-weight: 700;
`;

const HoverOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: linear-gradient(90deg, rgb(255, 140, 0), rgb(255, 215, 0));
  opacity: 0;
  transition: opacity 0.3s ease;
`;

const ShineEffect = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transform: translateX(-100%);
  transition: transform 0.5s ease;
`;

const PaymentSuccesful = () => {
  const handleClose = () => {
    // Add your close logic here
    console.log('Close button clicked');
    // Example: window.close() or navigate to another page
  };

  return (
    <Container>
      <BackgroundParticles>
        <Particle />
        <Particle />
        <Particle />
      </BackgroundParticles>

      <Card>
        <IconWrapper>
          <PingBorder />
          <CheckIcon viewBox="0 0 24 24">
            <polyline points="20,6 9,17 4,12"></polyline>
          </CheckIcon>
        </IconWrapper>
        
        <Title>Payment Successful!</Title>
        <Subtitle>
          Your payment has been processed successfully. 
          Thank you for your purchase.
        </Subtitle>
        
        <CloseButton onClick={handleClose}>
        <a href="https://betbazaar365.com"> <ButtonText>Close</ButtonText></a>
         
          <HoverOverlay className="hover-overlay" />
          <ShineEffect className="shine-effect" />
        </CloseButton>
      </Card>
    </Container>
  );
};

export default PaymentSuccesful;